import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, Phone, MapPin, UserPlus, ChevronDown, Check, Calendar, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEAMS = [
  { value: '10u', label: '10U' },
  { value: '11u', label: '11U' },
  { value: '12u', label: '12U' },
  { value: '13u', label: '13U' },
  { value: '14u', label: '14U' },
  { value: '2026', label: '2026' },
  { value: '2027', label: '2027' },
  { value: '2028', label: '2028' },
  { value: '2029', label: '2029' },
  { value: '2030', label: '2030' },
];

const AdminContacts = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [addingToTeam, setAddingToTeam] = useState(null);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('new');
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contact`);
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToTeam = async (submission, team) => {
    setAddingToTeam(submission.id);
    setDropdownOpen(null);
    
    try {
      const res = await axios.post(`${API}/admin/contact-to-player`, {
        contact_id: submission.id,
        team: team
      });
      
      const inviteMsg = res.data?.invite_sent
        ? ` Invite email sent to ${submission.email}.`
        : (res.data?.invite_skipped_reason === 'account_already_exists'
            ? ' (Invite skipped — account already exists.)'
            : '');
      setMessage({ type: 'success', text: `${submission.name} added to ${team.toUpperCase()} team!${inviteMsg}` });
      
      // Update submission status locally
      setSubmissions(prev => prev.map(s => 
        s.id === submission.id ? { ...s, status: 'added_to_team', added_to_team: team } : s
      ));
      
      // Clear message after 6 seconds
      setTimeout(() => setMessage(null), 6000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error adding to team' });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setAddingToTeam(null);
    }
  };

  const filteredSubmissions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return submissions.filter(s => {
      if (statusFilter === 'new' && s.added_to_team) return false;
      if (statusFilter === 'added' && !s.added_to_team) return false;
      if (q) {
        const hay = `${s.name || ''} ${s.email || ''} ${s.phone || ''} ${s.high_school || ''} ${s.positions || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [submissions, search, statusFilter]);

  return (
    <div className="page-content">
      <section className="page-hero">
        <div className="container">
          <Link to="/admin/dashboard" className="btn-secondary" style={{ marginBottom: '16px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <ArrowLeft size={20} /> Back to Dashboard
          </Link>
          <h1 className="display-large">Contact Form Submissions</h1>
          <p className="body-large">View player registration requests</p>
        </div>
      </section>

      <section style={{ padding: 'var(--spacing-xl) var(--spacing-medium)' }}>
        <div className="container">
          {/* Message Banner */}
          {message && (
            <div style={{
              padding: '16px 20px',
              marginBottom: '24px',
              borderRadius: '8px',
              background: message.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              border: `1px solid ${message.type === 'success' ? '#22c55e' : '#ef4444'}`,
              color: message.type === 'success' ? '#22c55e' : '#ef4444',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              {message.type === 'success' && <Check size={20} />}
              {message.text}
            </div>
          )}

          {loading ? (
            <p>Loading submissions...</p>
          ) : submissions.length === 0 ? (
            <div className="network-card">
              <p style={{ textAlign: 'center' }}>No submissions yet.</p>
            </div>
          ) : (
            <>
              {/* Search + Filter */}
              <div className="network-card" style={{ marginBottom: '20px', padding: '14px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: '1 1 260px', minWidth: '200px' }}>
                  <Search size={16} style={{ position: 'absolute', top: '50%', left: '12px', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, email, phone, school, position..."
                    style={{ width: '100%', padding: '10px 12px 10px 38px', background: 'rgba(255,255,255,0.06)', color: 'var(--text-primary)', border: '1px solid var(--border-medium)', borderRadius: '8px' }}
                    data-testid="contacts-search-input"
                  />
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {[{ v: 'all', l: 'All' }, { v: 'new', l: 'New' }, { v: 'added', l: 'Added to Team' }].map(f => (
                    <button
                      key={f.v}
                      onClick={() => setStatusFilter(f.v)}
                      data-testid={`contacts-filter-${f.v}`}
                      style={{
                        padding: '8px 14px',
                        background: statusFilter === f.v ? 'var(--brand-primary)' : 'rgba(255,255,255,0.06)',
                        color: statusFilter === f.v ? 'black' : 'var(--text-primary)',
                        border: '1px solid ' + (statusFilter === f.v ? 'var(--brand-primary)' : 'var(--border-medium)'),
                        borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer'
                      }}
                    >{f.l}</button>
                  ))}
                </div>
                <div style={{ marginLeft: 'auto', color: 'var(--text-light)', fontSize: '0.85rem' }}>
                  {filteredSubmissions.length} result{filteredSubmissions.length !== 1 ? 's' : ''}
                </div>
              </div>

              <div style={{ display: 'grid', gap: '24px' }}>
                {filteredSubmissions.length === 0 ? (
                  <div className="network-card"><p style={{ textAlign: 'center', color: 'var(--text-light)' }}>No submissions match your filters.</p></div>
                ) : filteredSubmissions.map(submission => (
                <div key={submission.id} className="network-card">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                    <div>
                      <h3 className="network-card-title">{submission.name}</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                        <Mail size={16} style={{ color: 'var(--brand-primary)' }} />
                        <span>{submission.email}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                        <Phone size={16} style={{ color: 'var(--brand-primary)' }} />
                        <span>{submission.phone}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                        <MapPin size={16} style={{ color: 'var(--brand-primary)' }} />
                        <span>{submission.address}</span>
                      </div>
                      {submission.date_of_birth && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                          <Calendar size={16} style={{ color: 'var(--brand-primary)' }} />
                          <span>DOB: {new Date(submission.date_of_birth).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <div className="detail-row">
                        <span className="detail-label">Position:</span>
                        <span className="detail-value">{submission.positions}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Height:</span>
                        <span className="detail-value">{submission.height}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Weight:</span>
                        <span className="detail-value">{submission.weight}</span>
                      </div>
                    </div>
                    
                    <div>
                      <div className="detail-row">
                        <span className="detail-label">High School:</span>
                        <span className="detail-value">{submission.high_school}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Grad Year:</span>
                        <span className="detail-value">{submission.graduation_year}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Submitted:</span>
                        <span className="detail-value">{new Date(submission.submitted_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  {submission.additional_info && (
                    <div style={{ marginTop: '16px', padding: '16px', background: 'var(--bg-subtle)', borderRadius: '12px' }}>
                      <strong>Additional Info:</strong>
                      <p style={{ marginTop: '8px' }}>{submission.additional_info}</p>
                    </div>
                  )}
                  
                  <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ padding: '8px 16px', background: 'var(--brand-primary)', color: 'var(--ghost-black)', borderRadius: '8px', display: 'inline-block', fontSize: '12px', fontWeight: 600 }}>
                      Status: {submission.status?.toUpperCase() || 'NEW'}
                    </div>
                    
                    {submission.added_to_team ? (
                      <div style={{ 
                        padding: '8px 16px', 
                        background: 'rgba(34, 197, 94, 0.2)', 
                        color: '#22c55e', 
                        borderRadius: '8px', 
                        display: 'inline-flex', 
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '12px', 
                        fontWeight: 600 
                      }}>
                        <Check size={14} /> Added to {submission.added_to_team.toUpperCase()} Team
                      </div>
                    ) : (
                      <div ref={dropdownOpen === submission.id ? dropdownRef : null} style={{ position: 'relative', zIndex: dropdownOpen === submission.id ? 1000 : 1 }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDropdownOpen(dropdownOpen === submission.id ? null : submission.id);
                          }}
                          disabled={addingToTeam === submission.id}
                          style={{
                            padding: '8px 16px',
                            background: 'rgba(59, 130, 246, 0.2)',
                            border: '1px solid rgba(59, 130, 246, 0.5)',
                            color: '#3b82f6',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '12px',
                            fontWeight: 600
                          }}
                        >
                          <UserPlus size={14} />
                          {addingToTeam === submission.id ? 'Adding...' : 'Add to Team'}
                          <ChevronDown size={14} />
                        </button>
                        
                        {dropdownOpen === submission.id && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            marginTop: '4px',
                            background: '#1a1a1a',
                            border: '1px solid #444',
                            borderRadius: '8px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.7)',
                            zIndex: 9999,
                            minWidth: '160px',
                            maxHeight: '320px',
                            overflowY: 'auto'
                          }}>
                            <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', fontSize: '0.75rem', color: '#999', fontWeight: 600, position: 'sticky', top: 0, background: '#1a1a1a' }}>
                              Select Team
                            </div>
                            <div style={{ padding: '4px 8px', fontSize: '0.7rem', color: '#666', borderBottom: '1px solid #333' }}>Youth Teams</div>
                            {TEAMS.filter(t => t.value.includes('u')).map((team) => (
                              <button
                                key={team.value}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddToTeam(submission, team.value);
                                }}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: '10px 12px',
                                  background: 'none',
                                  border: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  color: '#e5e5e5',
                                  fontSize: '0.875rem',
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(212, 175, 55, 0.2)'}
                                onMouseLeave={(e) => e.target.style.background = 'none'}
                              >
                                {team.label}
                              </button>
                            ))}
                            <div style={{ padding: '4px 8px', fontSize: '0.7rem', color: '#666', borderBottom: '1px solid #333', borderTop: '1px solid #333' }}>High School</div>
                            {TEAMS.filter(t => !t.value.includes('u')).map((team) => (
                              <button
                                key={team.value}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddToTeam(submission, team.value);
                                }}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: '10px 12px',
                                  background: 'none',
                                  border: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  color: '#e5e5e5',
                                  fontSize: '0.875rem',
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(212, 175, 55, 0.2)'}
                                onMouseLeave={(e) => e.target.style.background = 'none'}
                              >
                                {team.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Dropdown now uses outside-click via dropdownRef (no full-screen backdrop) */}
    </div>
  );
};

export default AdminContacts;
